<template>
  <Html :class="`theme-${config.public['theme']}`" :lang="language">
  <a href="#maincontent" class="skip-link">Skip to main content</a>
  <a href="#footercontent" class="skip-link">Skip to footer</a>
  <a href="#navcontent" class="skip-link">Skip to the main navigation</a>

  <Body :class="{
    'no-scroll': isModalOpen,
    'header-white': isError,
    'header-absolute': isError,
  }">
    <div :aria-hidden="isModalOpen">
      <component :is="header" :content="headerStory.content" />
      <slot />
      <PageAuthorBlock
        v-if="showAuthorBlock"
        :authors="pageAuthors"
      />
      <component :is="footer" :content="footerStory.content" />
      <button v-if="whatsappURL" @click="openWhatsApp" class="chat-btn whatsapp-btn" role="chat"
        aria-label="whatsapp">
        <SvgIcon name="icons8-whatsapp-48" />
      </button>
      <button v-if="showButton" @click="scrollToTop" :class="[
        chatActivated && countryPrefix == 'cl' ? 'go-to-top go-to-top-position' : goToTopStyle,
      ]" role="go-to-top" :aria-label="go_to_top_button_label">
        <SvgIcon name="func_up" />
      </button>
    </div>
    <div class="modals">
      <InquiryModal v-if="callbackOpened" ref="callbackModal" type="callback" :facility="facility" />
      <InquiryModal v-if="visitOpened" ref="visitModal" type="visit" :facility="facility" />
    </div>
    <StickyInformation v-if="shouldDisplaySticky && stickyStory" :blok="stickyStory.content" />
  </Body>

  </Html>
</template>

<script setup>
import GlobalHeader from "@/components/GlobalHeader.vue";
import GlobalFooter from "@/components/GlobalFooter.vue";
import StickyInformation from "@/storyblok/StickyInformation.vue";
import { VWO_SMART_CODE } from "@/constants/vwoSmartCode";
import { ref, onMounted, onBeforeUnmount } from "vue";

const config = useRuntimeConfig();
const trackingStore = useTrackingStore();

const { mutate: forwardClientLog } = useMutation(FORWARD_CLIENT_LOG);
const route = useRoute();
const datasourcesStore = useDatasourcesStore();
const language = config.public["language"]
  ? config.public["language"]
  : config.public["country"];

const go_to_top_button_label = useMicroCopy("goToTop.button.label");

const props = defineProps({
  isError: {
    type: Boolean,
    required: false,
    default: () => {
      return false;
    },
  },
  header: {
    type: Object,
    required: false,
    default: GlobalHeader,
  },
  footer: {
    type: Object,
    required: false,
    default: GlobalFooter,
  },
  pageStory: {
    type: Object,
    required: false,
    default: null,
  },
});

const tracking = (data) => {
  trackingStore.sendTrackingData(data);
};

watch(
  () => route?.path,
  (_route) => {
    if (typeof window !== "undefined") {
      setTimeout(() => {
        if (!isModalOpen.value) {
          document.body.classList.remove("no-scroll");
        }
        trackingStore.updateRoute(_route);
        if (window.google_tag_manager) {
          sendClientLog(forwardClientLog, "LOG", "gtm_api_ok", "GTM API available", maskKey(config.public["gtmId"]));
        }
        tracking({ event: "page.display" });
        if (window.Weglot && typeof window.Weglot.refresh === "function") {
          window.Weglot.refresh();
        }
      }, 500);
    }
  },
  { immediate: true }
);

const countryPrefix = config.public["storyblokCountryPrefix"];
const useDidomi =
  config.public["didomiApiKey"] || config.public["didomiApiKey"] != "";

const headerStory = ref(null);
const footerStory = ref(null);
const faviconStory = ref(null);
const resolvedPageStory = computed(() => toValue(props.pageStory));
const facility = computed(() => resolvedPageStory.value?.content ?? null);
const formsStory = ref(null);
const stickyStory = ref(null);
const isStickyActivated = datasourcesStore.getStickyContactActivated;

const isModalOpen = ref(false);

const updateIsModalOpen = async () => {
  await nextTick();
  isModalOpen.value = [...document.querySelectorAll(".modal")].some(
    (modal) => modal.getAttribute("aria-hidden") !== "true"
  );
};

provide("modal", { isModalOpen, updateIsModalOpen });

// Global callback modal
const callbackModal = ref(null);
const callbackOpened = ref(false);

const openCallbackModal = (injectValues) => {
  if (!callbackOpened.value) {
    callbackOpened.value = true;
    nextTick(() => {
      callbackModal.value?.openModal(injectValues);
    });
  } else {
    callbackModal.value?.openModal(injectValues);
  }
};

const visitModal = ref(null);
const visitOpened = ref(false);

const openVisitModal = () => {
  if (!visitOpened.value) {
    visitOpened.value = true;
    nextTick(() => {
      visitModal.value?.openModal();
    });
  } else {
    visitModal.value?.openModal();
  }
};

const openCallbackModalFromAnchor = () => {
  tracking({
    event: "click.action",
    click: "callback",
    click_chapter1: "anchor",
    click_chapter2: "CTA",
    form_name: getInquiryFormName("callback"),
  });
  openCallbackModal();
};

const openVisitModalFromAnchor = () => {
  tracking({
    event: "click.action",
    click: "visit",
    click_chapter1: "anchor",
    click_chapter2: "CTA",
    form_name: getInquiryFormName("visit"),
  });
  openVisitModal();
};

provide("callback", openCallbackModal);
provide("facility-callback", openCallbackModal);
provide("facility-visit", openVisitModal);

useAnchorFunction("#callback", openCallbackModalFromAnchor);
useAnchorFunction("#facility-callback", openCallbackModalFromAnchor);
useAnchorFunction("#facility-visit", openVisitModalFromAnchor);

if (!datasourcesStore.countrySettings?.length) {
  await datasourcesStore.fetchCountrySettings();
}

const genericNewsTemplates = parseStructuredDataTemplates(
  datasourcesStore.findCountrySetting("structured-data-generic-news")
);
const authorOverrideToPersonTemplates = parseStructuredDataTemplates(
  datasourcesStore.findCountrySetting(
    "structured-data-author-override-to-person"
  )
);

await useCustomAsyncStoryblok("", {
  version: config.public["storyblokVersion"],
  by_slugs: `${countryPrefix}/header,${countryPrefix}/footer,${countryPrefix}/favicon,${countryPrefix}/forms${isStickyActivated ? `,${countryPrefix}/sticky-information` : ""}`,
  resolve_links: "url",
}).then((result) => {
  headerStory.value = result.value.stories.find(
    (story) => story.slug === "header"
  );
  footerStory.value = result.value.stories.find(
    (story) => story.slug === "footer"
  );
  faviconStory.value = result.value.stories.find(
    (story) => story.slug === "favicon"
  );
  formsStory.value = result.value.stories.find(
    (story) => story.slug === "forms"
  );
  if (isStickyActivated) {
    stickyStory.value = result.value.stories.find(
      (story) => story.slug === "sticky-information"
    );
  }
});

const canonicalPath = () => {
  let routePath = route.path;
  if (routePath.slice(-1) === "/") routePath = routePath.slice(0, -1);
  return routePath;
};

const weglotApiKey = config.public?.["weglotApiKey"];
const weglotApiCountry = config.public?.["weglotApiCountry"]?.split(",");
const country = config.public["country"];
const requestURL = useRequestURL();
const pageUrl = () => requestURL.origin + canonicalPath();
const isHomepage = route.path === "/";

const {
  homepage: homepageStructuredDataSeo,
  webPage: webPageStructuredDataSeo,
  newsArticle: newsArticleStructuredDataSeo,
} = buildLayoutStructuredDataSeo({
  isHomepage: route.path === "/",
  headerContent: headerStory.value?.content,
  pageStory: resolvedPageStory.value,
  pageUrl: pageUrl(),
  baseURL: config.public["baseURL"],
  genericNewsTemplates,
  authorOverrideToPersonTemplates,
});

const pageAuthors = computed(() => getActiveAuthors(facility.value?.author));
const showAuthorBlock = computed(() => {
  const component = facility.value?.component;
  if (!component || genericNewsTemplates.includes(component)) {
    return false;
  }

  return shouldShowAuthorBlock(
    component,
    authorOverrideToPersonTemplates,
    pageAuthors.value
  );
});

const siteNavigationStructuredData = buildSiteNavigationStructuredData(
  headerStory.value.content
);
const siteNavigationStructuredDataSeo = siteNavigationStructuredData
  ? JSON.stringify(siteNavigationStructuredData)
  : null;

const useConvertExperiences =
  country === "nl" && datasourcesStore.getAbTestingConvertActivated;
const isVwoAbTestingActivated = datasourcesStore.getVwoAbTestingActivated && country === "es";


const scriptArray = [
    useConvertExperiences && {
      src: "https://cdn-4.convertexperiments.com/v1/js/100412076-100417882.js?environment=production",
      type: "text/javascript",
      tagPriority: 1e6,
    },
    useDidomi && {
      // Consent FIRST (before GTM). Async SDK, no <link rel=preload> vs LCP image.
      tagPriority: -10,
      innerHTML:
        `window.gdprAppliesGlobally=false;(function(){function n(e){if(!window.frames[e]){if(document.body&&document.body.firstChild){var t=document.body;var r=document.createElement("iframe");r.style.display="none";r.name=e;r.title=e;t.insertBefore(r,t.firstChild)}else{setTimeout(function(){n(e)},5)}}}function e(r,a,o,c,s){function e(e,t,r,n){if(typeof r!=="function"){return}if(!window[a]){window[a]=[]}var i=false;if(s){i=s(e,n,r)}if(!i){window[a].push({command:e,version:t,callback:r,parameter:n})}}e.stub=true;e.stubVersion=2;function t(n){if(!window[r]||window[r].stub!==true){return}if(!n.data){return}var i=typeof n.data==="string";var e;try{e=i?JSON.parse(n.data):n.data}catch(t){return}if(e[o]){var a=e[o];window[r](a.command,a.version,function(e,t){var r={};r[c]={returnValue:e,success:t,callId:a.callId};n.source.postMessage(i?JSON.stringify(r):r,"*")},a.parameter)}}if(typeof window[r]!=="function"){window[r]=e;if(window.addEventListener){window.addEventListener("message",t,false)}else{window.attachEvent("onmessage",t)}}}e("__tcfapi","__tcfapiBuffer","__tcfapiCall","__tcfapiReturn");n("__tcfapiLocator");(function(e,t){if(document.getElementById("spcloader"))return;var a=document.createElement("script");a.id="spcloader";a.async=true;a.charset="utf-8";var o="https://sdk.privacy-center.org/"+e+"/loader.js?target_type=notice&target="+t;if(window.didomiConfig&&window.didomiConfig.user){var c=window.didomiConfig.user;var s=c.country;var d=c.region;if(s){o=o+"&country="+s;if(d){o=o+"&region="+d}}}a.src=o;var f=document.getElementsByTagName("script")[0];f.parentNode.insertBefore(a,f)})("${config.public["didomiApiKey"]}","${config.public["didomiNoticeId"]}")})();`,
    },
    {
      // After consent stub/SDK: defer GTM so it does not race the LCP image.
      tagPriority: 20,
      innerHTML: `(function(w,d,s,l,i){function load(){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f)}function schedule(){if('requestIdleCallback'in w)w.requestIdleCallback(load,{timeout:3500});else setTimeout(load,2000)}if(d.readyState==='complete')schedule();else w.addEventListener('load',schedule)})(window,document,'script','dataLayer','${config.public["gtmId"]}');`,
    },
    isVwoAbTestingActivated && {
      id: "vwoCode",
      type: "text/javascript",
      tagPriority: 100,
      innerHTML: VWO_SMART_CODE,
    },
    weglotApiCountry && weglotApiKey && weglotApiCountry.includes(country) &&{
      src: "https://cdn.weglot.com/weglot.min.js",
      defer: true,
      tagPriority: 100,
    },
    weglotApiCountry && weglotApiKey && weglotApiCountry.includes(country) &&{
      innerHTML: `(function() {
        const urlLang = new URLSearchParams(window.location.search).get('lang');
        function initWeglot() {
          if (typeof Weglot !== 'undefined') {
            var mobileSlots = document.querySelectorAll('.weglot-switcher-mobile-slot');
            if (!document.querySelector('#weglot-switcher-desktop') || !mobileSlots.length) {
              setTimeout(initWeglot, 100);
              return;
            }
            var switcherStyle = {
              full_name: true,
              with_name: true,
              is_dropdown: true,
              with_flags: false,
              flag_type: 'rectangle_mat'
            };
            var switchers = [
              {
                button_style: switcherStyle,
                location: { target: '#weglot-switcher-desktop', sibling: null }
              }
            ];
            mobileSlots.forEach(function(el, i) {
              if (!el.id) {
                el.id = 'weglot-switcher-mobile-' + i;
              }
              switchers.push({
                button_style: switcherStyle,
                location: { target: '#' + el.id, sibling: null }
              });
            });
            Weglot.initialize({
              api_key: '${weglotApiKey}',
              switchers: switchers
            });
            if (typeof Weglot.on === 'function') {
              Weglot.on("languageChanged", function(newLang) {
                var defaultLang = '${language}';
                var match = document.cookie.match(/(?:^|;)\s*weglot_current_language=([^;]+)/);
                var weglotLang = match ? decodeURIComponent(match[1]) : null;
                if (newLang && newLang !== defaultLang && weglotLang === newLang) {
                  document.cookie = "wg_lang=" + newLang + "; path=/; max-age=31536000";
                } else {
                  document.cookie = "wg_lang=; path=/; max-age=0";
                }
              });
            }
            if (urlLang) {
              function switchLanguage() {
                if (typeof Weglot !== 'undefined' && typeof Weglot.switchTo === 'function') {
                  try {
                    Weglot.switchTo(urlLang);
                  } catch(e) {
                    console.error('Weglot switchTo error:', e);
                  }
                } else {
                  setTimeout(switchLanguage, 200);
                }
              }
              setTimeout(switchLanguage, 1000);
            }
          } else {
            setTimeout(initWeglot, 100);
          }
        }
        initWeglot();
      })();`,
    },
    homepageStructuredDataSeo && {
      type: "application/ld+json",
      innerHTML: homepageStructuredDataSeo,
    },
    webPageStructuredDataSeo && {
      type: "application/ld+json",
      innerHTML: webPageStructuredDataSeo,
    },
    newsArticleStructuredDataSeo && {
      type: "application/ld+json",
      innerHTML: newsArticleStructuredDataSeo,
    },
    siteNavigationStructuredDataSeo && {
      type: "application/ld+json",
      innerHTML: siteNavigationStructuredDataSeo,
    },
  ];

useHead({
  link: [
    useConvertExperiences && {
      rel: "preconnect",
      href: "https://cdn-4.convertexperiments.com",
    },
    isVwoAbTestingActivated && {
      rel: "preconnect",
      href: "https://dev.visualwebsiteoptimizer.com",
    },
    {
      rel: "canonical",
      href: useRequestURL().origin + canonicalPath(),
    },
    {
      rel: "apple-touch-icon",
      href: faviconStory.value.content.apple_touch_icon.filename,
    },
    {
      rel: "icon",
      href: faviconStory.value.content.icon_32.filename,
    },
    {
      rel: "icon",
      href: faviconStory.value.content.icon_16.filename,
    },
    {
      rel: "manifest",
      href: "/site.webmanifest",
    },
    {
      rel: "mask-icon",
      href: faviconStory.value.content.safari_pinned_tab.filename,
      color: `#${config.public["brandColor"]}`,
    },
  ],
  meta: [
    {
      name: "msapplication-TileColor",
      content: "#ffffff",
    },
    {
      name: "theme-color",
      content: "#ffffff",
    },
  ],
  // The reason we use .filter(Boolean) here is to ensure that only truthy values (i.e., actual script objects)
  // are included in the script array. This prevents issues arising from accidentally passing 'false', 'null',
  // or 'undefined' elements that might have been conditionally added to scriptArray earlier in the code.
  // For example, if certain scripts are only injected under specific config/feature flags, using .filter(Boolean)
  // makes sure we don't send extraneous, empty, or malformed <script> definitions to Nuxt's head configuration.
  script: scriptArray.filter(Boolean),
  noscript: [
    {
      innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.public["gtmId"]}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      tagPosition: "bodyOpen",
    },
  ],
});

const { isMobile: isMobileState, isDesktop: isDesktopState } = useDevice();
const cookie = useCookie('layout', {
  maxAge: undefined, // Cookie de session (expire à la fermeture du navigateur)
  sameSite: true
});

// Lecture du cookie au chargement
onMounted(() => {
  if (!config.public["gtmId"]) {
    sendClientLog(forwardClientLog, "ERROR", "gtm_api_error", "GTM API not available", maskKey(config.public["gtmId"]), "GTM ID not configured");
  }
  if (cookie.value !== undefined) {
    isMobileState.value = cookie.value === 'mobile';
  }
});

watchEffect(() => {
  if (isMobileState.value !== undefined) {
    cookie.value = isMobileState.value ? 'mobile' : 'desktop';
  }
});

provide("isMobile", isMobileState);
provide("isDesktop", isDesktopState);

// Auto scroll up
const showButton = ref(false);
const chatActivated = ref(false);
const goToTopStyle = ref("go-to-top-hidden");
const whatsappBtnStyle = ref("whatsapp-btn-hidden");
const whatsappURL = datasourcesStore.getWhatsappUrl;

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
  setTimeout(() => {
    if (window.scrollY > 200) {
      scrollToTop();
    }
  }, 100);
};

const handleScroll = () => {
  showButton.value = window.scrollY > 200;
};

onMounted(() => {
  showButton.value = true;
  setTimeout(() => {
    showButton.value = false;
    goToTopStyle.value = "go-to-top";
    whatsappBtnStyle.value = "whatsapp-btn";
  }, 1);
  window.addEventListener("scroll", handleScroll);

  const chatElement = document.getElementById("launcher");
  if (chatElement || whatsappURL) {
    chatActivated.value = true;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

const openWhatsApp = () => {
  window.open(whatsappURL, "_blank");
};

const shouldDisplaySticky = computed(() => {
  const isActivated = datasourcesStore.getStickyContactActivated;
  const excludedTemplates = datasourcesStore.getStickyContactExcludeTemplates;
  const currentTemplate = facility.value?.component;
  const routeSlug = route.path.split('/').filter(Boolean).pop();

  if (!isActivated) return false;

  if ((currentTemplate && excludedTemplates.includes(currentTemplate)) || (routeSlug && excludedTemplates.includes(routeSlug))) 
  {
    return false;
  }

  return true;
});
</script>

<style lang="scss" scoped>
.go-to-top-hidden {
  border: none;
  border-radius: 50%;
  display: flex;
}

.go-to-top {
  position: fixed;
  bottom: 57px;
  right: 24px;
  width: 50px;
  height: 50px;
  background-color: var(--solid-02);
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.go-to-top-position {
  bottom: 85px !important;
}

.go-to-top:hover {
  background-color: var(--solid-04);
  transform: translateY(-3px);
}

@include for-tablet-landscape-up {
  .go-to-top {
    width: 70px;
    height: 70px;
    bottom: 80px;
  }
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: var(--solid-02);
  padding: 8px 15px;
  z-index: 100;
  transition: top 0.3s;
  border-radius: 8px;
}

.skip-link:focus {
  top: 0;
}

.whatsapp-btn-hidden {
  position: fixed;
  bottom: 60px;
  left: 24px;
  border: none;
  background: none;
  display: flex;
  cursor: pointer;
  z-index: 1000;
}

.chat-btn {
  position: fixed;
  bottom: 37px;
  left: 0;
  border: none;
  background: none;
  display: flex;
  cursor: pointer;
  transition: transform 0.2s;
  z-index: 1000;
}

.whatsapp-btn {
  background-color: #25d366;
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  left: 20px;
  width: 50px;
  height: 50px;
  bottom: 85px;


  @include for-tablet-landscape-up {
    width: 70px;
    height: 70px;
  }
}

.whatsapp-btn:hover {
  transform: scale(1.1);
}
</style>
